import React, { useEffect, useState, useMemo } from 'react';
import {
  Gauge, Building2, MapPin, Briefcase, TrendingUp, ChevronDown, ChevronUp,
  Loader2, Zap, Globe, Target, Activity,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import { TierBadge, ConfidenceBadge } from '../components/TierBadges';
import { CompanyActionMenu } from '../components/CompanyActionMenu';
import { QuickOutreachModal } from '../components/pipelines/QuickOutreachModal';
import { CompanyEnrichModal } from '../components/CompanyEnrichModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  'Very High': { color: 'bg-red-100 text-red-700 border-red-200', bar: '#dc2626', dot: 'bg-red-500' },
  'High': { color: 'bg-orange-100 text-orange-700 border-orange-200', bar: '#ea580c', dot: 'bg-orange-500' },
  'Moderate': { color: 'bg-amber-100 text-amber-700 border-amber-200', bar: '#d97706', dot: 'bg-amber-500' },
  'Low': { color: 'bg-gray-100 text-gray-600 border-gray-200', bar: '#6b7280', dot: 'bg-gray-400' },
};

const VELOCITY_COLORS = {
  'Surge': 'text-red-600', 'Aggressive': 'text-orange-600',
  'Active': 'text-amber-600', 'Steady': 'text-gray-500',
};

const STATE_ABBREVS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A1F44] text-white px-3 py-2 rounded-lg text-xs shadow-xl">
      <p className="font-medium">{label}</p>
      <p>{payload[0].value.toLocaleString()} jobs</p>
    </div>
  );
};

const HiringPressure = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('pressure_score');
  const [sortDir, setSortDir] = useState('desc');
  const [tierFilter, setTierFilter] = useState('all');
  const [outreachCompany, setOutreachCompany] = useState(null);
  const [outreachTarget, setOutreachTarget] = useState(null);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  const fetchData = () => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/hiring-pressure?limit=200${filterParams}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [excludeStaffing, excludeRetail]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const openOutreach = (company) => setOutreachCompany(company);

  const companies = data?.companies || [];
  const filtered = tierFilter === 'all' ? companies : companies.filter(c => c.pressure_tier === tierFilter);
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  }), [filtered, sortField, sortDir]);

  const tiers = data?.tier_distribution || {};
  const stateData = (data?.by_state || []).map(s => ({
    ...s, abbrev: STATE_ABBREVS[s.state] || s.state,
  }));
  const industryData = data?.by_industry || [];
  const topCompChart = data?.top_companies_chart || [];

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-3 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Hiring Pressure" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-red-50 rounded-xl flex items-center justify-center">
            <Gauge className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="hiring-pressure-title">Hiring Pressure Intelligence</h1>
            <p className="text-sm text-gray-500">Companies under significant hiring demand — prioritize for outreach</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Calculating hiring pressure scores...
          </div>
        ) : (
          <>
            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="pressure-metrics">
              <MetricCard label="Companies Analyzed" value={data?.total_analyzed || 0} icon={Building2} />
              <MetricCard label="Very High Pressure" value={tiers['Very High'] || 0} icon={Zap}
                subtext={`${tiers['Very High'] > 0 ? 'Urgent targets' : 'None detected'}`} />
              <MetricCard label="High Pressure" value={tiers['High'] || 0} icon={Target} />
              <MetricCard label="Moderate" value={tiers['Moderate'] || 0} icon={Activity} />
              <MetricCard label="Low" value={tiers['Low'] || 0} icon={Globe} />
            </div>

            {/* Tier Filter */}
            <div className="flex items-center gap-3" data-testid="tier-filters">
              <Gauge className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 font-medium">Filter:</span>
              {['all', 'Very High', 'High', 'Moderate', 'Low'].map(tier => (
                <button key={tier} onClick={() => setTierFilter(tier)} data-testid={`filter-${tier.toLowerCase().replace(' ', '-')}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${tierFilter === tier
                    ? 'bg-[#0A1F44] text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}>
                  {tier === 'all' ? `All (${companies.length})` : `${tier} (${tiers[tier] || 0})`}
                </button>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="pressure-charts">
              {/* Top Companies Bar Chart */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Top Hiring Companies</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topCompChart.slice(0, 12)} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="company" width={120} tick={{ fontSize: 9 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="jobs" radius={[0, 4, 4, 0]}>
                      {topCompChart.slice(0, 12).map((_, i) => (
                        <Cell key={i} fill={i < 3 ? '#dc2626' : i < 6 ? '#ea580c' : '#d97706'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* State Distribution */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Hiring Demand by State</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stateData.slice(0, 15)} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="abbrev" width={35} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} labelFormatter={(l) => {
                      const match = stateData.find(s => s.abbrev === l);
                      return match?.state || l;
                    }} />
                    <Bar dataKey="jobs" fill="#0A1F44" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Industry Distribution */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Hiring Demand by Industry</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={industryData.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="industry" width={100} tick={{ fontSize: 9 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="jobs" radius={[0, 4, 4, 0]}>
                      {industryData.slice(0, 10).map((_, i) => {
                        const colors = ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#059669', '#0891b2', '#2563eb', '#7c3aed', '#be185d'];
                        return <Cell key={i} fill={colors[i % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Main Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="pressure-table">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-[#0A1F44]">Top Companies by Hiring Pressure</h3>
                <p className="text-xs text-gray-400 mt-0.5">{sorted.length} companies | Last 30 days</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-3 py-3 w-8">#</th>
                      <SortHeader field="company" label="Company" />
                      <th className="text-center px-3 py-3 text-xs">Tier</th>
                      <SortHeader field="total_jobs" label="Jobs (30d)" align="right" />
                      <SortHeader field="cities_count" label="Cities" align="right" />
                      <th className="text-left px-3 py-3 text-xs">Industry</th>
                      <SortHeader field="avg_jobs_per_day" label="Jobs/Day" align="right" />
                      <th className="text-center px-3 py-3 text-xs">Volume</th>
                      <th className="text-center px-3 py-3 text-xs">Velocity</th>
                      <SortHeader field="pressure_score" label="Score" align="center" />
                      <th className="text-center px-3 py-3 text-xs">Pressure</th>
                      <th className="text-center px-3 py-3 text-xs">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => {
                      const tier = TIER_CONFIG[item.pressure_tier] || TIER_CONFIG['Low'];
                      return (
                        <tr key={item.company} className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors"
                          data-testid={`pressure-row-${i}`}>
                          <td className="px-3 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <WatchlistButton company={item.company} sourceModule="hiring-pressure" />
                              <div>
                                <div className="font-semibold text-[#0A1F44] text-sm">{item.company}</div>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[10px] text-gray-400">{item.top_cities?.slice(0, 2).join(', ')}</span>
                                  <ConfidenceBadge confidence={item.firmographic_confidence} flags={item.confidence_flags} />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center"><TierBadge tier={item.priority_tier} /></td>
                          <td className="px-3 py-3 text-right font-bold text-[#0A1F44]">{item.total_jobs.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{item.cities_count}</td>
                          <td className="px-3 py-3">
                            {item.industry ? (
                              <div className="flex flex-col gap-1">
                                <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span>
                                <CompanyTypeBadge type={item.company_type} />
                              </div>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-right text-gray-600">{item.avg_jobs_per_day}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-[10px] font-medium ${item.volume_label === 'Very High' ? 'text-red-600' : item.volume_label === 'High' ? 'text-orange-600' : 'text-gray-500'}`}>
                              {item.volume_label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-[10px] font-medium ${VELOCITY_COLORS[item.velocity_label] || 'text-gray-500'}`}>
                              {item.velocity_label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <div className="w-10 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${item.pressure_score}%`, backgroundColor: tier.bar }} />
                              </div>
                              <span className="text-xs font-bold text-[#0A1F44] w-6">{item.pressure_score}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${tier.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} />
                              {item.pressure_tier}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => openOutreach(item.company)} data-testid={`outreach-pressure-${i}`}
                                className="px-3 py-1.5 bg-[#0A1F44] text-white rounded-lg text-[10px] font-semibold hover:bg-[#0d2a5c] transition-colors">
                                Outreach
                              </button>
                              <CompanyActionMenu
                                companyName={item.company}
                                companyData={{ industry: item.industry }}
                                onOutreach={(c) => setOutreachTarget(c)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scoring Legend */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="scoring-legend">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-3">Hiring Pressure Score Methodology</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Job Volume (35 pts)</p>
                  <p>1-4 = Low | 5-9 = Moderate | 10-24 = High | 25+ = Very High</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Hiring Velocity (35 pts)</p>
                  <p>Surge = 50+ in 7d | Aggressive = 20+ in 7d | Active = 10+ | Steady</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Geographic Spread (30 pts)</p>
                  <p>National = 10+ cities | Regional = 3-9 | Localized = 1-2</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                {['Very High', 'High', 'Moderate', 'Low'].map(tier => (
                  <span key={tier} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold border ${TIER_CONFIG[tier].color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${TIER_CONFIG[tier].dot}`} />
                    {tier}: {tier === 'Very High' ? '85-100' : tier === 'High' ? '65-84' : tier === 'Moderate' ? '45-64' : '0-44'}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      {/* Outreach Modal */}
      {outreachCompany && <CompanyEnrichModal company={outreachCompany} onClose={() => setOutreachCompany(null)} />}
      {outreachTarget && <QuickOutreachModal company={outreachTarget} onClose={() => setOutreachTarget(null)} onSent={() => setOutreachTarget(null)} />}
    </IntelligenceLayout>
  );
};

export default HiringPressure;
